class ShoppableVideoCarousel {
  constructor(section) {
    this.section = section;
    this.container = section.querySelector('.video-carousel__container');
    this.modal = document.getElementById(`VideoModal-${section.dataset.sectionId}`);
    
    if (!this.container || !this.modal) return;

    // Store all video cards for navigation
    this.cards = Array.from(this.container.querySelectorAll('.video-carousel__card'));
    this.currentIndex = 0;

    this.setupThumbnails();
    this.setupModal();
    this.setupAutoplay();
    this.setupSwipeNavigation();
  }

  setupThumbnails() {
    this.cards.forEach((card, index) => {
      card.addEventListener('click', () => {
        this.currentIndex = index;
        const videoData = this.getVideoData(card);
        this.openModal(videoData);
      });
    });
  }

  getVideoData(card) {
    return {
      videoSrc: card.dataset.videoSrc,
      productHandle: card.dataset.productHandle,
      productTitle: card.dataset.productTitle,
      productPrice: card.dataset.productPrice,
      productImage: card.dataset.productImage,
      productUrl: card.dataset.productUrl
    };
  }

  setupAutoplay() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target.querySelector('video');
        if (!video) return;

        if (entry.isIntersecting) {
          video.play().catch(e => console.log('Autoplay prevented:', e));
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.5 });

    this.cards.forEach(card => observer.observe(card));
  }

  setupModal() {
    this.modalVideo = this.modal.querySelector('.video-modal__video');
    this.closeBtn = this.modal.querySelector('.video-modal__close');
    this.backdrop = this.modal.querySelector('.video-modal__backdrop');
    this.modalContent = this.modal.querySelector('.video-modal__content');
    
    this.modalImage = this.modal.querySelector('.video-modal__product-image');
    this.modalTitle = this.modal.querySelector('.video-modal__product-title');
    this.modalPrice = this.modal.querySelector('.video-modal__product-price');
    this.modalBtn = this.modal.querySelector('.video-modal__add-btn');

    const closeHandler = () => this.closeModal();

    this.closeBtn.addEventListener('click', closeHandler);
    this.backdrop.addEventListener('click', closeHandler);
    
    // Close on Escape, navigate on Arrow keys
    document.addEventListener('keydown', (e) => {
      if (!this.modal.classList.contains('is-open')) return;
      
      if (e.key === 'Escape') {
        closeHandler();
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        this.navigatePrev();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        this.navigateNext();
      }
    });
  }

  setupSwipeNavigation() {
    let touchStartY = 0;
    let touchStartX = 0;
    let touchEndY = 0;
    let touchEndX = 0;
    const minSwipeDistance = 50;

    this.modal.addEventListener('touchstart', (e) => {
      touchStartY = e.changedTouches[0].screenY;
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.modal.addEventListener('touchend', (e) => {
      touchEndY = e.changedTouches[0].screenY;
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartY, touchEndY, touchStartX, touchEndX, minSwipeDistance);
    }, { passive: true });
  }

  handleSwipe(startY, endY, startX, endX, minDistance) {
    const diffY = startY - endY;
    const diffX = startX - endX;
    
    // Only trigger if vertical swipe is more prominent than horizontal
    if (Math.abs(diffY) > Math.abs(diffX) && Math.abs(diffY) > minDistance) {
      if (diffY > 0) {
        // Swipe up - go to next video
        this.navigateNext();
      } else {
        // Swipe down - go to previous video
        this.navigatePrev();
      }
    }
  }

  navigateNext() {
    if (this.currentIndex < this.cards.length - 1) {
      this.currentIndex++;
      this.transitionToVideo('up');
    } else {
      // Reached the end - show bounce effect
      this.showBounce('bottom');
    }
  }

  navigatePrev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.transitionToVideo('down');
    } else {
      // At the start - show bounce effect
      this.showBounce('top');
    }
  }

  transitionToVideo(direction) {
    const card = this.cards[this.currentIndex];
    const videoData = this.getVideoData(card);
    
    // Animate out
    const translateOut = direction === 'up' ? '-100%' : '100%';
    const translateIn = direction === 'up' ? '100%' : '-100%';
    
    if (this.modalContent) {
      this.modalContent.style.transition = 'transform 0.3s ease-out';
      this.modalContent.style.transform = `translateY(${translateOut})`;
      
      setTimeout(() => {
        // Update content
        this.updateModalContent(videoData);
        
        // Reset and animate in
        this.modalContent.style.transition = 'none';
        this.modalContent.style.transform = `translateY(${translateIn})`;
        
        requestAnimationFrame(() => {
          this.modalContent.style.transition = 'transform 0.3s ease-out';
          this.modalContent.style.transform = 'translateY(0)';
        });
      }, 150);
    } else {
      this.updateModalContent(videoData);
    }

    // Update navigation indicator
    this.updateNavigationIndicator();
  }

  showBounce(edge) {
    if (!this.modalContent) return;
    const offset = edge === 'top' ? '20px' : '-20px';
    this.modalContent.style.transition = 'transform 0.15s ease-out';
    this.modalContent.style.transform = `translateY(${offset})`;
    setTimeout(() => {
      this.modalContent.style.transform = 'translateY(0)';
    }, 150);
  }

  updateModalContent(data) {
    this.modalVideo.src = data.videoSrc;
    this.modalImage.src = data.productImage;
    this.modalImage.style.display = data.productImage ? 'block' : 'none';
    this.modalTitle.textContent = data.productTitle;
    this.modalPrice.textContent = data.productPrice;
    this.modalBtn.href = data.productUrl;
    
    this.modalVideo.muted = false;
    this.modalVideo.currentTime = 0;
    this.modalVideo.play().catch(e => console.log('Video play error:', e));
  }

  updateNavigationIndicator() {
    let indicator = this.modal.querySelector('.video-modal__nav-indicator');
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'video-modal__nav-indicator';
      this.modal.querySelector('.video-modal__content')?.appendChild(indicator);
    }
    indicator.innerHTML = `${this.currentIndex + 1} / ${this.cards.length}`;
  }

  showSwipeHint() {
    let hint = this.modal.querySelector('.video-modal__swipe-hint');
    if (!hint) {
      hint = document.createElement('div');
      hint.className = 'video-modal__swipe-hint';
      hint.innerHTML = `
        <div class="swipe-hint__content">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="18,15 12,9 6,15"></polyline>
          </svg>
          <span>Swipe up for next</span>
        </div>
      `;
      this.modal.querySelector('.video-modal__content')?.appendChild(hint);
    }

    // Show hint briefly
    hint.classList.add('visible');
    setTimeout(() => {
      hint.classList.remove('visible');
    }, 2500);
  }

  openModal(data) {
    this.updateModalContent(data);
    document.body.style.overflow = 'hidden';
    this.modal.classList.add('is-open');
    
    // Reset transition
    if (this.modalContent) {
      this.modalContent.style.transition = '';
      this.modalContent.style.transform = '';
    }
    
    // Show navigation indicator
    this.updateNavigationIndicator();
    
    // Show swipe hint on first open
    if (!this.hasShownHint) {
      setTimeout(() => this.showSwipeHint(), 500);
      this.hasShownHint = true;
    }
  }

  closeModal() {
    document.body.style.overflow = '';
    this.modal.classList.remove('is-open');
    
    setTimeout(() => {
      this.modalVideo.pause();
      this.modalVideo.src = "";
    }, 300);
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.section-video-carousel');
  sections.forEach(section => new ShoppableVideoCarousel(section));
});
